import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dialog, IconButton } from "@mui/material";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Filter,
  Mail,
  Menu,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import axios from "axios";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import SimpleSnackbar from "../Snackbar";
import { isAdminEmail } from "../../utils/adminAccess";

const defaultFilters = {
  search: "",
  sex: "all",
  paymentStatus: "",
  plan: "all",
  assignedStatus: "",
  marketingConsent: "",
  complaintStatus: "",
  complaint: "",
  startDate: "",
  endDate: "",
  minConsultations: "",
  maxConsultations: "",
  minPrescriptions: "",
  maxPrescriptions: "",
  minInvestigations: "",
  maxInvestigations: "",
  minMessages: "",
  maxMessages: "",
};

const fallbackTemplates = [
  {
    key: "checking_in",
    title: "Checking in",
    subject: "Checking in from Zaviramed",
    body:
      "We are checking in to know how you are feeling today.\n\nIf you still need medical support, please reply on WhatsApp or book a consultation through Zaviramed. We are here to help.",
  },
  {
    key: "custom",
    title: "Custom email",
    subject: "",
    body: "",
  },
];

const fallbackWhatsappTemplates = [
  {
    key: "patient_follow_up",
    title: "Follow up on patient",
    description: "Use when a patient has been quiet and needs a care check-in.",
    body:
      "Hi {{firstName}}, we have not heard from you in a while. If you still need medical support, please send us a message here and the Zaviramed care team will assist you.",
  },
  {
    key: "new_message_reminder",
    title: "New message reminder",
    description: "Use when the care team has sent a message and needs a reply.",
    body:
      "Hi {{firstName}}, you have received a new message from the Zaviramed care team. Please reply here to continue the conversation.",
  },
];

const deliveryOptions = [
  { value: "whatsapp", label: "WhatsApp template only" },
  { value: "email", label: "Email only" },
  { value: "both", label: "WhatsApp template and email" },
];

const formatNumber = (value) =>
  new Intl.NumberFormat("en-NG").format(Number(value || 0));

const paymentOptions = [
  { value: "", label: "All payment history" },
  { value: "paid_active", label: "Paid and active" },
  { value: "paid_before", label: "Has paid before" },
  { value: "paid_expired", label: "Paid but expired" },
  { value: "never_paid", label: "Never paid" },
];

const emailInputClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-400";

const labelClass = "mb-2 block text-xs font-medium text-slate-500";

const AdminEmailsPage = ({ adminMode = false }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [templates, setTemplates] = useState(fallbackTemplates);
  const [whatsappTemplates, setWhatsappTemplates] = useState(
    fallbackWhatsappTemplates
  );
  const [variables, setVariables] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("checking_in");
  const [selectedWhatsappTemplateKey, setSelectedWhatsappTemplateKey] =
    useState("patient_follow_up");
  const [deliveryChannel, setDeliveryChannel] = useState("whatsapp");
  const [step, setStep] = useState("recipients");
  const [fromName, setFromName] = useState("Zaviramed Care Team");
  const [subject, setSubject] = useState(fallbackTemplates[0].subject);
  const [body, setBody] = useState(fallbackTemplates[0].body);
  const [filters, setFilters] = useState(defaultFilters);
  const [filterOpen, setFilterOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [totalRecipients, setTotalRecipients] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedRecipients, setSelectedRecipients] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendProgress, setSendProgress] = useState({
    label: "Ready",
    percent: 0,
  });
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const token = localStorage.getItem("token") || "";
  const isAdmin = isAdminEmail(doctor.email);
  const pageTitle = adminMode ? "Admin Reminders" : "Reminders";
  const pageDescription = adminMode
    ? "Send follow-up reminders to one client, a filtered group, or all matching users."
    : "Send follow-up reminders to your assigned patients by WhatsApp template, email, or both.";
  const reminderApiBase = useMemo(
    () => `${process.env.REACT_APP_BACKEND_URL}/reminders`,
    []
  );

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const selectedCount = selectedIds.size;
  const targetCount = selectedCount;
  const selectedRecipientList = useMemo(
    () => Object.values(selectedRecipients),
    [selectedRecipients]
  );

  const showSnack = (message, color = "success") => {
    setSnackMessage(message);
    setSnackColor(color);
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const res = await axios.get(
        `${reminderApiBase}/templates`,
        { headers }
      );

      const nextTemplates = res.data?.templates?.length
        ? res.data.templates
        : fallbackTemplates;
      const nextWhatsappTemplates = res.data?.whatsappTemplates?.length
        ? res.data.whatsappTemplates
        : fallbackWhatsappTemplates;

      setTemplates(nextTemplates);
      setWhatsappTemplates(nextWhatsappTemplates);
      setVariables(res.data?.variables || []);
      setSelectedWhatsappTemplateKey(nextWhatsappTemplates[0]?.key || "");

      const initial =
        nextTemplates.find((item) => item.key === "checking_in") ||
        nextTemplates[0];

      if (initial) {
        setSelectedTemplateKey(initial.key);
        setSubject(initial.subject || "");
        setBody(initial.body || "");
      }
    } catch (error) {
      console.error(error);
      showSnack(
        error.response?.data?.message || "Could not load reminder templates",
        "error"
      );
    } finally {
      setLoadingTemplates(false);
    }
  }, [headers, reminderApiBase]);

  const previewRecipients = useCallback(async () => {
    try {
      setLoadingRecipients(true);
      const res = await axios.post(
        `${reminderApiBase}/recipients`,
        { filters },
        { headers }
      );

      const nextRecipients = res.data?.recipients || [];
      setRecipients(nextRecipients);
      setTotalRecipients(Number(res.data?.total || 0));
    } catch (error) {
      console.error(error);
      showSnack(
        error.response?.data?.message || "Could not load reminder recipients",
        "error"
      );
    } finally {
      setLoadingRecipients(false);
    }
  }, [filters, headers, reminderApiBase]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    previewRecipients();
  }, [previewRecipients]);

  const applyTemplate = (templateKey) => {
    const template = templates.find((item) => item.key === templateKey);
    setSelectedTemplateKey(templateKey);

    if (!template) return;

    setSubject(template.subject || "");
    setBody(template.body || "");
    setSendResult(null);
  };

  const toggleRecipient = (recipient) => {
    const id = recipient?._id;
    if (!id) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setSelectedRecipients((items) => {
          const copy = { ...items };
          delete copy[id];
          return copy;
        });
      } else {
        next.add(id);
        setSelectedRecipients((items) => ({
          ...items,
          [id]: recipient,
        }));
      }
      return next;
    });
  };

  const selectVisibleRecipients = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      recipients.forEach((item) => {
        if (item?._id) next.add(item._id);
      });
      return next;
    });
    setSelectedRecipients((prev) => {
      const next = { ...prev };
      recipients.forEach((item) => {
        if (item?._id) next[item._id] = item;
      });
      return next;
    });
  };

  const selectAllMatchingRecipients = async () => {
    try {
      setSelectingAll(true);
      const res = await axios.post(
        `${reminderApiBase}/recipients`,
        { filters, includeAll: true },
        { headers }
      );

      const nextRecipients = res.data?.recipients || [];

      if (!nextRecipients.length) {
        showSnack("No clients found for this selection", "warning");
        return;
      }

      setSelectedIds((prev) => {
        const next = new Set(prev);
        nextRecipients.forEach((item) => {
          if (item?._id) next.add(item._id);
        });
        return next;
      });

      setSelectedRecipients((prev) => {
        const next = { ...prev };
        nextRecipients.forEach((item) => {
          if (item?._id) next[item._id] = item;
        });
        return next;
      });

      showSnack(
        `${formatNumber(nextRecipients.length)} client${
          nextRecipients.length === 1 ? "" : "s"
        } added to the selected list`
      );
    } catch (error) {
      console.error(error);
      showSnack(
        error.response?.data?.message || "Could not select all users",
        "error"
      );
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedRecipients({});
  };

  const removeSelectedRecipient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedRecipients((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSendResult(null);
    setStep("recipients");
  };

  const continueToComposer = () => {
    if (!targetCount) {
      showSnack("Choose at least one recipient first", "error");
      return;
    }

    setStep("compose");
    setSendResult(null);
  };

  const sendEmails = async () => {
    const shouldSendEmail =
      deliveryChannel === "email" || deliveryChannel === "both";
    const shouldSendWhatsapp =
      deliveryChannel === "whatsapp" || deliveryChannel === "both";

    if (shouldSendEmail && !fromName.trim()) {
      showSnack("Sender name is required", "error");
      return;
    }

    if (shouldSendEmail && (!subject.trim() || !body.trim())) {
      showSnack("Subject and body are required", "error");
      return;
    }

    if (shouldSendWhatsapp && !selectedWhatsappTemplateKey) {
      showSnack("Choose a WhatsApp template", "error");
      return;
    }

    if (!targetCount) {
      showSnack("Select at least one client or select the full filtered group", "error");
      return;
    }

    try {
      setSending(true);
      setSendResult(null);
      setSendProgress({ label: "Preparing recipients", percent: 25 });

      const payload = {
        fromName,
        subject,
        body,
        channel: deliveryChannel,
        whatsappTemplateKey: selectedWhatsappTemplateKey,
        selectAllMatching: false,
        filters: {},
        clientIds: [...selectedIds],
      };

      setSendProgress({ label: "Sending reminders", percent: 65 });

      const res = await axios.post(
        `${reminderApiBase}/send`,
        payload,
        { headers }
      );

      setSendResult(res.data);
      setSendProgress({ label: "Delivered", percent: 100 });
      showSnack(res.data?.message || "Reminder request completed", res.data?.success ? "success" : "warning");
    } catch (error) {
      console.error(error);
      setSendProgress({ label: "Failed", percent: 100 });
      showSnack(
        error.response?.data?.message || "Could not send reminders",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  if (adminMode && !isAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <DoctorHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-1 items-center px-4 py-10">
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-amber-500" />
              <div>
                <h1 className="text-lg font-semibold text-slate-950">
                  Admin access only
                </h1>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  This reminders page is only available to Zaviramed admin
                  accounts.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />
      <SimpleSnackbar message={snackMessage} color={snackColor} />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">
            {adminMode ? "Admin" : "Doctor"}
          </p>
          <h1 className="text-base font-semibold text-slate-950">
            {pageTitle}
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab={pageTitle} setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab={pageTitle}
          setActiveTab={() => setDrawerOpen(false)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="hidden text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl lg:block">
                  {pageTitle}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  {pageDescription}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={previewRecipients}
                  disabled={loadingRecipients}
                  startIcon={<RefreshCw size={16} />}
                  sx={darkButtonStyles}
                >
                  {loadingRecipients ? "Loading..." : "Refresh"}
                </Button>
              </div>
            </div>

            <StepHeader step={step} />

            {step === "recipients" ? (
              <div className="space-y-5">
                <section className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-950">
                        Find clients
                      </p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Showing {formatNumber(recipients.length)} of{" "}
                        {formatNumber(totalRecipients)} matching clients. Use
                        filters, add clients, filter again, and keep stacking.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => setFilterOpen(true)}
                        startIcon={<Filter size={16} />}
                        sx={lightButtonStyles}
                      >
                        Filter clients
                      </Button>
                      <Button
                        onClick={previewRecipients}
                        disabled={loadingRecipients}
                        startIcon={<RefreshCw size={16} />}
                        sx={darkButtonStyles}
                      >
                        {loadingRecipients ? "Loading..." : "Refresh"}
                      </Button>
                    </div>
                  </div>
                </section>

                <SelectedRecipientsBar
                  selectedRecipients={selectedRecipientList}
                  onRemove={removeSelectedRecipient}
                  onClear={clearSelection}
                  onWriteEmail={continueToComposer}
                  loading={loadingRecipients}
                />

                <RecipientPanel
                  recipients={recipients}
                  selectedIds={selectedIds}
                  totalRecipients={totalRecipients}
                  loading={loadingRecipients}
                  selectingAll={selectingAll}
                  onToggle={toggleRecipient}
                  onSelectVisible={selectVisibleRecipients}
                  onSelectAll={selectAllMatchingRecipients}
                />

              </div>
            ) : (
              <div className="space-y-5">
                <AudienceSummary
                  targetCount={targetCount}
                  selectedCount={selectedCount}
                  onBack={() => setStep("recipients")}
                />

                <EmailComposer
                  templates={templates}
                  whatsappTemplates={whatsappTemplates}
                  variables={variables}
                  selectedTemplateKey={selectedTemplateKey}
                  selectedWhatsappTemplateKey={selectedWhatsappTemplateKey}
                  setSelectedWhatsappTemplateKey={
                    setSelectedWhatsappTemplateKey
                  }
                  deliveryChannel={deliveryChannel}
                  setDeliveryChannel={setDeliveryChannel}
                  loadingTemplates={loadingTemplates}
                  fromName={fromName}
                  setFromName={setFromName}
                  subject={subject}
                  body={body}
                  setSubject={setSubject}
                  setBody={setBody}
                  applyTemplate={applyTemplate}
                  targetCount={targetCount}
                  sending={sending}
                  sendEmails={sendEmails}
                  onBack={() => setStep("recipients")}
                />

                <ProgressPanel
                  sending={sending}
                  progress={sendProgress}
                  result={sendResult}
                />
              </div>
            )}

            <FilterModal
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              filters={filters}
              updateFilter={updateFilter}
              resetFilters={resetFilters}
              loading={loadingRecipients}
              matchingCount={totalRecipients}
              visibleCount={recipients.length}
              onRefresh={previewRecipients}
            />
          </div>
        </main>
      </div>

    </div>
  );
};

const darkButtonStyles = {
  minHeight: 44,
  borderRadius: "12px",
  bgcolor: "#0f172a",
  color: "white",
  px: 3,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: "#1e293b", boxShadow: "none" },
  "&:disabled": {
    bgcolor: "#e2e8f0",
    color: "#64748b",
  },
};

const lightButtonStyles = {
  minHeight: 40,
  borderRadius: "12px",
  borderColor: "#e2e8f0",
  color: "#334155",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": {
    borderColor: "#cbd5e1",
    bgcolor: "#f8fafc",
    boxShadow: "none",
  },
};

const FilterModal = ({
  open,
  onClose,
  filters,
  updateFilter,
  resetFilters,
  loading,
  matchingCount,
  visibleCount,
  onRefresh,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    PaperProps={{
      sx: {
        borderRadius: "24px",
        overflow: "hidden",
        m: { xs: 1.5, sm: 3 },
      },
    }}
  >
    <div className="border-b border-slate-100 bg-white px-4 py-4 sm:px-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-950">
            Filter clients
          </h2>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            {formatNumber(visibleCount)} shown from{" "}
            {formatNumber(matchingCount)} matching clients.
          </p>
        </div>

        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </div>
    </div>

    <div className="max-h-[72vh] overflow-y-auto bg-slate-50 p-4 sm:p-5">
      <FilterPanel
        filters={filters}
        updateFilter={updateFilter}
        resetFilters={resetFilters}
        loading={loading}
      />
    </div>

    <div className="flex flex-col gap-2 border-t border-slate-100 bg-white px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
      <Button onClick={onClose} sx={lightButtonStyles}>
        Close
      </Button>
      <Button
        onClick={() => {
          onRefresh();
          onClose();
        }}
        disabled={loading}
        startIcon={<RefreshCw size={16} />}
        sx={darkButtonStyles}
      >
        {loading ? "Loading..." : "Apply filter"}
      </Button>
    </div>
  </Dialog>
);

const StepHeader = ({ step }) => {
  const steps = [
    { key: "recipients", label: "Select recipients" },
    { key: "compose", label: "Send reminder" },
  ];

  return (
    <section className="mb-5 rounded-2xl border border-slate-200 bg-white p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {steps.map((item, index) => {
          const active = step === item.key;
          const done = step === "compose" && item.key === "recipients";

          return (
            <div
              key={item.key}
              className={`flex items-center gap-3 rounded-xl px-3 py-3 ${
                active
                  ? "bg-slate-900 text-white"
                  : done
                  ? "bg-emerald-50 text-emerald-800"
                  : "bg-slate-50 text-slate-500"
              }`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  active
                    ? "bg-white text-slate-900"
                    : done
                    ? "bg-emerald-600 text-white"
                    : "bg-white text-slate-500"
                }`}
              >
                {done ? <CheckCircle2 size={15} /> : index + 1}
              </span>
              <span className="text-sm font-semibold">{item.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const AudienceSummary = ({
  targetCount,
  selectedCount,
  onBack,
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-slate-950">
          Sending to {formatNumber(targetCount)} client
          {targetCount === 1 ? "" : "s"}
        </p>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {formatNumber(selectedCount)} selected client
          {selectedCount === 1 ? "" : "s"} will receive this reminder.
        </p>
      </div>

      <Button
        onClick={onBack}
        startIcon={<ArrowLeft size={16} />}
        sx={lightButtonStyles}
      >
        Edit recipients
      </Button>
    </div>
  </section>
);

const FilterPanel = ({ filters, updateFilter, resetFilters, loading }) => {
  const [open, setOpen] = useState({
    main: true,
    extra: false,
    activity: false,
  });

  const toggle = (key) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-950">
              Choose clients
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Start simple. Open more filters only when needed.
            </p>
          </div>
          <Button
            onClick={resetFilters}
            disabled={loading}
            sx={lightButtonStyles}
          >
            Reset
          </Button>
        </div>
      </div>

      <div className="space-y-3 p-4">
        <FoldableSection
          title="Main filters"
          hint="Search, sex, payment and plan"
          open={open.main}
          onToggle={() => toggle("main")}
        >
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Search</label>
              <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 px-3">
                <Search size={16} className="text-slate-400" />
                <input
                  value={filters.search}
                  onChange={(event) =>
                    updateFilter("search", event.target.value)
                  }
                  placeholder="Name, email, phone, complaint"
                  className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Sex">
                <select
                  value={filters.sex}
                  onChange={(event) => updateFilter("sex", event.target.value)}
                  className={emailInputClass}
                >
                  <option value="all">All sex</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </Field>

              <Field label="Payment">
                <select
                  value={filters.paymentStatus}
                  onChange={(event) =>
                    updateFilter("paymentStatus", event.target.value)
                  }
                  className={emailInputClass}
                >
                  {paymentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Plan">
                <select
                  value={filters.plan}
                  onChange={(event) => updateFilter("plan", event.target.value)}
                  className={emailInputClass}
                >
                  <option value="all">All plans</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                </select>
              </Field>
            </div>
          </div>
        </FoldableSection>

        <FoldableSection
          title="More filters"
          hint="Complaint, doctor, consent and date"
          open={open.extra}
          onToggle={() => toggle("extra")}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Assigned doctor">
                <select
                  value={filters.assignedStatus}
                  onChange={(event) =>
                    updateFilter("assignedStatus", event.target.value)
                  }
                  className={emailInputClass}
                >
                  <option value="">All clients</option>
                  <option value="assigned">Assigned</option>
                  <option value="unassigned">Unassigned</option>
                </select>
              </Field>

              <Field label="Marketing consent">
                <select
                  value={filters.marketingConsent}
                  onChange={(event) =>
                    updateFilter("marketingConsent", event.target.value)
                  }
                  className={emailInputClass}
                >
                  <option value="">All clients</option>
                  <option value="yes">Consented</option>
                  <option value="no">No consent recorded</option>
                </select>
              </Field>

              <Field label="Complaint">
                <select
                  value={filters.complaintStatus}
                  onChange={(event) =>
                    updateFilter("complaintStatus", event.target.value)
                  }
                  className={emailInputClass}
                >
                  <option value="">All complaints</option>
                  <option value="has">Has complaint</option>
                  <option value="none">No complaint</option>
                </select>
              </Field>
            </div>

            <Field label="Complaint contains">
              <input
                value={filters.complaint}
                onChange={(event) =>
                  updateFilter("complaint", event.target.value)
                }
                placeholder="Headache, diabetes, pregnancy..."
                className={emailInputClass}
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Created from">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) =>
                    updateFilter("startDate", event.target.value)
                  }
                  className={emailInputClass}
                />
              </Field>
              <Field label="Created to">
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) =>
                    updateFilter("endDate", event.target.value)
                  }
                  className={emailInputClass}
                />
              </Field>
            </div>
          </div>
        </FoldableSection>

        <FoldableSection
          title="Activity numbers"
          hint="Consultations, prescriptions, investigations and messages"
          open={open.activity}
          onToggle={() => toggle("activity")}
        >
          <div className="space-y-4">
            <CountRange
              label="Consultations"
              minKey="minConsultations"
              maxKey="maxConsultations"
              filters={filters}
              updateFilter={updateFilter}
            />
            <CountRange
              label="Prescriptions"
              minKey="minPrescriptions"
              maxKey="maxPrescriptions"
              filters={filters}
              updateFilter={updateFilter}
            />
            <CountRange
              label="Investigations"
              minKey="minInvestigations"
              maxKey="maxInvestigations"
              filters={filters}
              updateFilter={updateFilter}
            />
            <CountRange
              label="Messages"
              minKey="minMessages"
              maxKey="maxMessages"
              filters={filters}
              updateFilter={updateFilter}
            />
          </div>
        </FoldableSection>
      </div>
    </section>
  );
};

const FoldableSection = ({ title, hint, open, onToggle, children }) => (
  <div className="rounded-xl border border-slate-200">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
    >
      <span>
        <span className="block text-sm font-semibold text-slate-950">
          {title}
        </span>
        {hint && (
          <span className="mt-0.5 block text-xs leading-5 text-slate-500">
            {hint}
          </span>
        )}
      </span>
      <ChevronDown
        size={17}
        className={`shrink-0 text-slate-400 transition ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>

    {open && <div className="border-t border-slate-100 p-3">{children}</div>}
  </div>
);

const CountRange = ({ label, minKey, maxKey, filters, updateFilter }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <div className="grid grid-cols-2 gap-3">
      <input
        type="number"
        min="0"
        value={filters[minKey]}
        onChange={(event) => updateFilter(minKey, event.target.value)}
        placeholder="Min"
        className={emailInputClass}
      />
      <input
        type="number"
        min="0"
        value={filters[maxKey]}
        onChange={(event) => updateFilter(maxKey, event.target.value)}
        placeholder="Max"
        className={emailInputClass}
      />
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const SelectedRecipientsBar = ({
  selectedRecipients = [],
  onRemove,
  onClear,
  onWriteEmail,
  loading,
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-base font-semibold text-slate-950">
          Selected recipients
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Pick patients from different filters. They will keep stacking here.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {selectedRecipients.length > 0 && (
          <Button
            onClick={onWriteEmail}
            disabled={loading}
            startIcon={<Mail size={16} />}
            sx={darkButtonStyles}
          >
            Send reminder
          </Button>
        )}

        <Button
          onClick={onClear}
          disabled={!selectedRecipients.length}
          startIcon={<Trash2 size={15} />}
          sx={lightButtonStyles}
        >
          Clear list
        </Button>
      </div>
    </div>

    {selectedRecipients.length ? (
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {selectedRecipients.map((recipient) => (
          <div
            key={recipient._id}
            className="flex min-w-[240px] items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-950">
                {recipient.name || "Patient"}
              </p>
              <p className="truncate text-xs text-slate-500">
                {recipient.email || recipient.phoneNumber || "No contact"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(recipient._id)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-white hover:text-slate-900"
              aria-label={`Remove ${recipient.name || recipient.email}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    ) : (
      <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        No clients selected yet.
      </div>
    )}
  </section>
);

const RecipientPanel = ({
  recipients,
  selectedIds,
  totalRecipients,
  loading,
  selectingAll,
  onToggle,
  onSelectVisible,
  onSelectAll,
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white">
    <div className="border-b border-slate-100 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-950">
            Recipients
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Showing {formatNumber(recipients.length)} of{" "}
            {formatNumber(totalRecipients)} matching clients. Select all adds
            every matching user, including those not shown in this table.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onSelectAll}
            disabled={loading || selectingAll || !totalRecipients}
            startIcon={<Users size={16} />}
            sx={darkButtonStyles}
          >
            {selectingAll ? "Selecting..." : "Select all users"}
          </Button>
          <Button
            onClick={onSelectVisible}
            disabled={loading || !recipients.length}
            sx={lightButtonStyles}
          >
            Add shown to list
          </Button>
        </div>
      </div>
    </div>

    <div className="overflow-x-auto">
      {loading ? (
        <TableSkeleton />
      ) : recipients.length ? (
        <table className="min-w-[1120px] w-full divide-y divide-slate-100 text-left">
          <thead className="bg-slate-50">
            <tr>
              <TableHead>Add</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Sex / age</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Complaint</TableHead>
              <TableHead>Activity</TableHead>
              <TableHead>Plan</TableHead>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {recipients.map((recipient) => {
              const checked = selectedIds.has(recipient._id);
              return (
                <tr
                  key={recipient._id}
                  className={checked ? "bg-emerald-50/50" : "hover:bg-slate-50"}
                >
                  <td className="w-20 px-4 py-4">
                    <button
                      type="button"
                      onClick={() => onToggle(recipient)}
                      className={`inline-flex h-9 min-w-[72px] items-center justify-center rounded-full px-3 text-xs font-semibold transition ${
                        checked
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-900 text-white hover:bg-slate-700"
                      }`}
                    >
                      {checked ? "Added" : "Add"}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <p className="max-w-[220px] truncate text-sm font-semibold text-slate-950">
                      {recipient.name || "Patient"}
                    </p>
                    <p className="mt-1 max-w-[240px] truncate text-xs text-slate-500">
                      {recipient.email || "No email recorded"}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    {recipient.phoneNumber || "N/A"}
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    {recipient.sex || "N/A"}
                    {recipient.age ? ` · ${recipient.age}` : ""}
                  </td>
                  <td className="px-4 py-4">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {recipient.paymentStatus?.label || "Payment unknown"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="line-clamp-2 max-w-[280px] text-sm leading-5 text-slate-600">
                      {recipient.chiefComplaint || "No complaint recorded"}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                      <Pill>{recipient.emailStats?.consultations || 0} consults</Pill>
                      <Pill>{recipient.emailStats?.prescriptions || 0} Rx</Pill>
                      <Pill>{recipient.emailStats?.investigations || 0} tests</Pill>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm capitalize text-slate-600">
                    {recipient.plan || "free"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="m-4 rounded-xl border border-dashed border-slate-200 p-6 text-center">
          <Mail className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            No clients found
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Loosen the filters and refresh the recipient list.
          </p>
        </div>
      )}
    </div>
  </section>
);

const TableHead = ({ children }) => (
  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
    {children}
  </th>
);

const EmailComposer = ({
  templates,
  whatsappTemplates,
  variables,
  selectedTemplateKey,
  selectedWhatsappTemplateKey,
  setSelectedWhatsappTemplateKey,
  deliveryChannel,
  setDeliveryChannel,
  loadingTemplates,
  fromName,
  setFromName,
  subject,
  body,
  setSubject,
  setBody,
  applyTemplate,
  targetCount,
  sending,
  sendEmails,
  onBack,
}) => {
  const shouldShowEmail =
    deliveryChannel === "email" || deliveryChannel === "both";
  const shouldShowWhatsapp =
    deliveryChannel === "whatsapp" || deliveryChannel === "both";
  const selectedWhatsappTemplate =
    whatsappTemplates.find((item) => item.key === selectedWhatsappTemplateKey) ||
    whatsappTemplates[0];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-950">
              Send reminder
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Choose WhatsApp template, email, or both before sending.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={onBack}
              startIcon={<ArrowLeft size={16} />}
              sx={lightButtonStyles}
            >
              Back
            </Button>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {formatNumber(targetCount)} target{targetCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <Field label="Send by">
          <select
            value={deliveryChannel}
            onChange={(event) => setDeliveryChannel(event.target.value)}
            className={emailInputClass}
          >
            {deliveryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        {shouldShowWhatsapp && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <Field label="WhatsApp template">
              <select
                value={selectedWhatsappTemplateKey}
                onChange={(event) =>
                  setSelectedWhatsappTemplateKey(event.target.value)
                }
                disabled={loadingTemplates}
                className={emailInputClass}
              >
                {whatsappTemplates.map((template) => (
                  <option key={template.key} value={template.key}>
                    {template.title}
                  </option>
                ))}
              </select>
            </Field>

            {selectedWhatsappTemplate && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  WhatsApp preview
                </p>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">
                  {selectedWhatsappTemplate.body}
                </p>
                {selectedWhatsappTemplate.description && (
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {selectedWhatsappTemplate.description}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {shouldShowEmail && (
          <div className="space-y-4 rounded-xl border border-slate-200 p-4">
            <Field label="Sender name">
              <input
                value={fromName}
                onChange={(event) => setFromName(event.target.value)}
                placeholder="Zaviramed Care Team"
                className={emailInputClass}
              />
            </Field>

            <Field label="Email template">
              <select
                value={selectedTemplateKey}
                onChange={(event) => applyTemplate(event.target.value)}
                disabled={loadingTemplates}
                className={emailInputClass}
              >
                {templates.map((template) => (
                  <option key={template.key} value={template.key}>
                    {template.title}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Subject">
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Email subject"
                className={emailInputClass}
              />
            </Field>

            <Field label="Body">
              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="Write the email body here"
                rows={10}
                className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
              />
            </Field>

            {variables.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-slate-500">
                  Personal fields you can use
                </p>
                <div className="flex flex-wrap gap-2">
                  {variables.map((variable) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() =>
                        setBody((prev) => `${prev}${prev ? " " : ""}${variable}`)
                      }
                      className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Email preview
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-950">
                {subject || "Subject will show here"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                From: {fromName || "Sender name will show here"}
              </p>
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">
                {body || "Body will show here"}
              </p>
            </div>
          </div>
        )}

        <Button
          onClick={sendEmails}
          disabled={sending || !targetCount}
          startIcon={<Send size={16} />}
          fullWidth
          sx={darkButtonStyles}
        >
          {sending
            ? "Sending..."
            : `Send reminder to ${formatNumber(targetCount)} client${
                targetCount === 1 ? "" : "s"
              }`}
        </Button>
      </div>
    </section>
  );
};

const ProgressPanel = ({ sending, progress, result }) => {
  const totals = result?.totals || {};
  const failures =
    result?.results?.filter((item) =>
      ["failed", "partial"].includes(item.status)
    ) || [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-950">
            Delivery progress
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {sending ? progress.label : result ? result.message : "No reminder sent yet."}
          </p>
        </div>
        {result?.success ? (
          <CheckCircle2 className="text-emerald-600" />
        ) : result ? (
          <AlertCircle className="text-amber-500" />
        ) : (
          <Mail className="text-slate-300" />
        )}
      </div>

      <div className="mt-5 h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${
            result?.success ? "bg-emerald-600" : "bg-slate-900"
          }`}
          style={{ width: `${Math.max(progress.percent || 0, result ? 100 : 0)}%` }}
        />
      </div>

      {result && (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-5">
          <MiniStat label="Attempted" value={totals.attempted || 0} />
          <MiniStat label="Delivered" value={totals.delivered || 0} />
          <MiniStat label="Partial" value={totals.partial || 0} />
          <MiniStat label="Failed" value={totals.failed || 0} />
          <MiniStat
            label="Email / WhatsApp"
            value={`${formatNumber(totals.emailDelivered || 0)} / ${formatNumber(
              totals.whatsappDelivered || 0
            )}`}
          />
        </div>
      )}

      {failures.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            Failed or partial reminders
          </p>
          <div className="mt-3 space-y-2">
            {failures.slice(0, 8).map((item) => (
              <div key={item.patientId} className="text-xs leading-5 text-amber-900">
                <span className="font-semibold">{item.email}</span>:{" "}
                {item.error || "Could not send"}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className="mt-1 text-xl font-semibold text-slate-950">
      {typeof value === "number" ? formatNumber(value) : value}
    </p>
  </div>
);

const Pill = ({ children }) => (
  <span className="rounded-full bg-slate-100 px-2 py-1">{children}</span>
);

const TableSkeleton = () => (
  <div className="min-w-[1120px] p-4">
    <div className="animate-pulse space-y-3">
      <div className="grid grid-cols-[80px_260px_160px_120px_160px_280px_220px_100px] gap-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-3 rounded bg-slate-200" />
        ))}
      </div>

      {Array.from({ length: 7 }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="grid grid-cols-[80px_260px_160px_120px_160px_280px_220px_100px] gap-4 rounded-xl border border-slate-100 p-3"
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className={`h-4 rounded ${
                index === 5 ? "bg-slate-100" : "bg-slate-200"
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default AdminEmailsPage;
